import {
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
  DEFAULT_ADD_LINE_MODE,
  DEFAULT_ADD_POLYGON_MODE,
  DEFAULT_ADD_SURFACE_MODE,
  resetAllCreationToolsAtom,
} from '@/atoms/viewer.atom';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

export type CreationTool = 'point' | 'line' | 'polygon' | 'surface';

/**
 * Per-layer toggles for the four geometry-creation tools. Toggling a tool on
 * for a layer always disables the other three (mutual exclusion); toggling
 * the same tool again deactivates it. Used by the `+` popover on each
 * VECTOR layer row in Layers.tsx.
 *
 * `activeToolForLayer` is a small derived helper for showing an active-state
 * badge on the popover trigger button.
 */
export const useExclusiveCreationTool = (layerId: string) => {
  const [addPointMode, setAddPointMode] = useAtom(addPointModeAtom);
  const [addLineMode, setAddLineMode] = useAtom(addLineModeAtom);
  const [addPolygonMode, setAddPolygonMode] = useAtom(addPolygonModeAtom);
  const [addSurfaceMode, setAddSurfaceMode] = useAtom(addSurfaceModeAtom);
  const resetAll = useSetAtom(resetAllCreationToolsAtom);

  const isPointActive = addPointMode.enabled && addPointMode.targetLayerId === layerId;
  const isLineActive = addLineMode.enabled && addLineMode.targetLayerId === layerId;
  const isPolygonActive = addPolygonMode.enabled && addPolygonMode.targetLayerId === layerId;
  const isSurfaceActive = addSurfaceMode.enabled && addSurfaceMode.targetLayerId === layerId;

  const activeToolForLayer: CreationTool | null = isPointActive
    ? 'point'
    : isLineActive
      ? 'line'
      : isPolygonActive
        ? 'polygon'
        : isSurfaceActive
          ? 'surface'
          : null;

  const togglePoint = useCallback(() => {
    if (isPointActive) {
      setAddPointMode({ enabled: false, targetLayerId: null, draftFeature: null });
      return;
    }
    resetAll();
    setAddPointMode({ enabled: true, targetLayerId: layerId, draftFeature: null });
  }, [isPointActive, layerId, resetAll, setAddPointMode]);

  const toggleLine = useCallback(() => {
    if (isLineActive) {
      setAddLineMode({ ...DEFAULT_ADD_LINE_MODE });
      return;
    }
    resetAll();
    setAddLineMode({ ...DEFAULT_ADD_LINE_MODE, enabled: true, targetLayerId: layerId });
  }, [isLineActive, layerId, resetAll, setAddLineMode]);

  const togglePolygon = useCallback(() => {
    if (isPolygonActive) {
      setAddPolygonMode({ ...DEFAULT_ADD_POLYGON_MODE });
      return;
    }
    resetAll();
    setAddPolygonMode({ ...DEFAULT_ADD_POLYGON_MODE, enabled: true, targetLayerId: layerId });
  }, [isPolygonActive, layerId, resetAll, setAddPolygonMode]);

  const toggleSurface = useCallback(() => {
    if (isSurfaceActive) {
      setAddSurfaceMode({ ...DEFAULT_ADD_SURFACE_MODE });
      return;
    }
    resetAll();
    setAddSurfaceMode({
      ...DEFAULT_ADD_SURFACE_MODE,
      enabled: true,
      targetLayerId: layerId,
      phase: 'picking-1',
    });
  }, [isSurfaceActive, layerId, resetAll, setAddSurfaceMode]);

  return {
    activeToolForLayer,
    isPointActive,
    isLineActive,
    isPolygonActive,
    isSurfaceActive,
    togglePoint,
    toggleLine,
    togglePolygon,
    toggleSurface,
  };
};
